import React from 'react';

import { NextPage } from 'next';
import { useRouter } from 'next/router';

import { NewsListPageProps } from '@domains/view';

import {
    makeAllArticlesRoute,
    makeCatalogRoute,
    makeNewsArticlesRoute,
    makeNewsPromotionsRoute,
} from '@utils/route';
import { applyTypograf } from '@utils/typograf';

import { DefaultLayout } from '@layouts/DefaultLayout/DefaultLayout';

import { Breadcrumbs } from '@components/common/Breadcrumbs/Breadcrumbs';
import { Chips, ChipsKind } from '@components/common/Chip/Chips';
import { Container } from '@components/common/Container/Container';
import { InformingBlock } from '@components/common/InformingBlock/InformingBlock';
import { NewsGrid } from '@components/common/NewsGrid/NewsGrid';
import { Pagination } from '@components/common/Pagination/Pagination';

import styles from './News.module.scss';

import { mockNewsViewBreadcrumbs } from '@mock/news';

type PageVariant = 'all' | 'promotion' | 'news';

const title = 'Акции и новости';

export const News: NextPage<NewsListPageProps> = (props) => {
    const { data } = props;
    const { items, page, page_size, total, seo } = data;

    const router = useRouter();
    const query = router.query.type as PageVariant;

    const pageVariants: PageVariant[] = ['all', 'promotion', 'news'];
    const pageVariant = pageVariants.includes(query) ? query : 'all';

    const emptyStateTitles: Record<PageVariant, string> = {
        all: applyTypograf('Скоро тут появятся акции и новости') as string,
        promotion: applyTypograf('Скоро тут появятся акции') as string,
        news: applyTypograf('Скоро тут появятся новости') as string,
    };

    const emptyStateTitle = emptyStateTitles[pageVariant];

    const chipsData = [
        {
            id: 'all',
            url: makeAllArticlesRoute(),
            caption: 'Все',
        },
        {
            id: 'promotion',
            url: makeNewsPromotionsRoute(),
            caption: 'Акции',
        },
        {
            id: 'news',
            url: makeNewsArticlesRoute(),
            caption: 'Новости',
        },
    ];

    return (
        <DefaultLayout {...seo}>
            <Container>
                <Breadcrumbs items={mockNewsViewBreadcrumbs} isWithHomeCrumb />
                <h1 className={styles.title}>{applyTypograf(title)}</h1>
            </Container>
            <Chips
                kind={ChipsKind.Links}
                data={chipsData}
                activeChipId={pageVariant}
                className={styles.chips}
            />
            <Container>
                {items.length > 0 ? (
                    <>
                        <NewsGrid news={items} />
                        {total > page_size && (
                            <Pagination
                                options={{
                                    currentQuery: {
                                        type: router.query.type,
                                    },
                                }}
                                className={styles.pagination}
                                currentPage={page}
                                pageSize={page_size}
                                total={total}
                            />
                        )}
                    </>
                ) : (
                    <div className={styles.informing}>
                        <InformingBlock
                            icon="empty"
                            title={{
                                text: emptyStateTitle,
                                element: 'h1',
                                marginBottom: 40,
                            }}
                            description={{
                                text: 'Но в каталоге очень много интересного!',
                                color: 'black',
                                size: 20,
                                marginBottom: 16,
                            }}
                            buttons={[
                                {
                                    link: makeCatalogRoute(),
                                    variant: 'green',
                                    children: 'В каталог',
                                },
                                null,
                            ]}
                        />
                    </div>
                )}
            </Container>
        </DefaultLayout>
    );
};
